import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams, Link } from 'react-router-dom';
import { debounce } from "throttle-debounce";
import '@fortawesome/fontawesome-free/css/all.min.css';
import api from 'api';
import Util from 'utility';
import Loading from 'components/Loading/index';
import { __ } from 'locale';

const recipeItemCls = 'flex items-center border-t border-t-[#f0f0f0] py-[0.85em] hover:bg-[#fafafa] max-[639px]:items-start';
const pictureCls = 'basis-[48px] h-[48px] mr-[0.75em] bg-center bg-no-repeat bg-cover rounded-full cursor-pointer shrink-0 max-[639px]:basis-[56px] max-[639px]:h-[56px] max-[639px]:mt-[4px]';
const nutrDataCls = 'flex w-[560px] text-[0.85em] justify-center pr-[2em] max-[829px]:w-full max-[829px]:basis-full max-[639px]:flex-wrap [&>div]:inline-flex [&>div]:items-center [&>div]:justify-center [&>div]:basis-[calc((100%-100px)/5)] [&>div]:pr-[1em] [&>div>span]:hidden [&>div:last-child]:basis-[100px] max-[829px]:[&>div]:flex-wrap max-[829px]:[&>div]:justify-start max-[829px]:[&>div]:text-[0.9em] max-[829px]:[&>div>span]:block max-[829px]:[&>div>span]:basis-full max-[829px]:[&>div>span]:text-left max-[829px]:[&>div>span]:text-[0.8em] max-[829px]:[&>div>span]:text-[#555] max-[639px]:[&>div]:basis-[33.33%] max-[639px]:[&>div:last-child]:basis-[33.33%] max-[639px]:[&>div]:mb-[0.5em]';
const filtersItemCls = 'flex flex-wrap mb-[1em] [&>label]:basis-[calc(25%-1em)] [&>label]:min-w-[170px] [&>label]:mr-[1em] [&>label]:py-[2px] [&>label]:inline-flex [&>label]:items-center [&>label]:whitespace-nowrap [&>label]:cursor-pointer [&>label:hover]:text-[#26AA52] [&>label>input]:hidden [&>label>span]:inline-flex [&>label>span]:items-center [&>label>span]:justify-center [&>label>span]:text-[0.85em] [&>label>span]:w-[1.6em] [&>label>span]:h-[1.6em] [&>label>span]:bg-white [&>label>span]:border [&>label>span]:border-[#26AA52] [&>label>span]:text-white [&>label>span]:mr-[4px] [&>label>span]:rounded-[20px] [&>label>input:checked+span]:bg-[#26AA52]';
const blLink = (isActive) => `text-[1em] py-[0.2em] px-[0.3em] rounded border border-transparent ml-[5px] cursor-pointer no-underline ${isActive ? 'text-[#26AA52] bg-[#e9f5e5]' : 'bg-transparent text-black hover:border-[#ccc]'}`;

function RecipeSearch({ onClick, isEmbedded, isDrinkRegime, newRecipeLink }) {
    const data = useSelector(state => state);
    const history = useHistory();
    const { action } = useParams();

    const getInitialFilter = () => {
        let initialUserId = ["0"];
        if (action === 'me') initialUserId = ["" + data.user.id];

        let f = {
            q: '',
            user_id: initialUserId,
            tags: [],
            sortBy: '',
            sortOrder: '',
            lastSortIsCustom: false,
            customSortAttribute: 'fiber',
            viewMode: 'list',
            nutritionCalcMode: 'serving',
            nutritionSortMode: 'popular',
        };

        if (isDrinkRegime) {
            f.tags = [3, 106];
        }

        return f;
    };

    const [filter, setFilter] = useState(getInitialFilter);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [perPage] = useState(30);
    const [loading, setLoading] = useState(false);
    const [recipes, setRecipes] = useState({});

    const filterRef = useRef(filter);
    const pageRef = useRef(0);
    const loadRecipesRef = useRef(null);

    const loadRecipes = () => {
        setLoading(true);
        const f = { ...filterRef.current, appMode: data.appMode };
        api('recipes/list', { post: true, data: { page: pageRef.current, filter: f } }).then(res => {
            setLoading(false);
            setTotal(res.data.total);
            setRecipes(res.data.recipes);
        });
    };
    loadRecipesRef.current = loadRecipes;

    const loadRecipesDebounced = useRef(debounce(250, () => loadRecipesRef.current()));

    useEffect(() => {
        loadRecipesRef.current();
    }, []); // eslint-disable-line

    const updateFilter = (e) => {
        const v = e.target.value;
        const newFilter = { ...filterRef.current };
        switch (e.target.name) {
            case 'q':
                newFilter.q = v;
                break;
            case 'u':
                if (typeof isEmbedded === 'undefined') {
                    if (v == 0) {
                        history.push('/recipe');
                    } else {
                        history.push('/recipe/me');
                    }
                }
                newFilter.user_id = [v];
                break;
            case 't':
                if (newFilter.tags.includes(v)) {
                    newFilter.tags = newFilter.tags.filter(item => item !== v);
                } else {
                    newFilter.tags = [...newFilter.tags, v];
                }
                break;
            default:
                break;
        }
        filterRef.current = newFilter;
        pageRef.current = 0;
        setFilter(newFilter);
        setPage(0);
        loadRecipesDebounced.current();
    };

    const updateSort = (e) => {
        const newFilter = { ...filterRef.current };
        let newSortBy = e.currentTarget.getAttribute('data-sort');
        if (newSortBy === 'custom') {
            if (e.currentTarget.getAttribute('data-change-order-only') === '1') {
                newSortBy = newFilter.sortBy;
                newFilter.sortOrder = newFilter.sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
                newSortBy = e.target.value;
                newFilter.customSortAttribute = newSortBy;
                newFilter.sortBy = newSortBy;
                newFilter.sortOrder = 'desc';
            }
            newFilter.lastSortIsCustom = true;
        } else {
            newFilter.lastSortIsCustom = false;
            if (newSortBy === newFilter.sortBy) {
                switch (newFilter.sortOrder) {
                    case 'asc':
                        newFilter.sortOrder = 'desc';
                        break;
                    case 'desc':
                        newFilter.sortOrder = '';
                        newFilter.sortBy = '';
                        break;
                    default:
                        break;
                }
            } else {
                newFilter.sortBy = newSortBy;
                newFilter.sortOrder = 'asc';
            }
        }
        filterRef.current = newFilter;
        pageRef.current = 0;
        setFilter(newFilter);
        setPage(0);
        loadRecipesDebounced.current();
    };

    const setViewMode = (mode) => {
        const newFilter = { ...filterRef.current, viewMode: mode };
        filterRef.current = newFilter;
        setFilter(newFilter);
        setPage(0);
        pageRef.current = 0;
    };

    const setNutritionCalcMode = (mode) => {
        const newFilter = { ...filterRef.current, nutritionCalcMode: mode };
        filterRef.current = newFilter;
        setFilter(newFilter);
        setPage(0);
        pageRef.current = 0;
        loadRecipesDebounced.current();
    };

    const setNutritionSortMode = (mode) => {
        const newFilter = { ...filterRef.current, nutritionSortMode: mode };
        filterRef.current = newFilter;
        setFilter(newFilter);
        loadRecipesDebounced.current();
    };

    const changePage = (e, newPage) => {
        pageRef.current = newPage;
        setPage(newPage);
        loadRecipesRef.current();
    };

    const { globals } = data;

    let sortSymbols = {};
    if (filter.sortBy !== '') {
        if (filter.sortOrder === 'asc') {
            sortSymbols[filter.sortBy] = <i className="fa fa-angle-up"></i>;
        } else {
            sortSymbols[filter.sortBy] = <i className="fa fa-angle-down"></i>;
        }
    }

    let sortSymbolCustom = false;
    if (filter.lastSortIsCustom) {
        if (filter.sortOrder === 'asc') {
            sortSymbolCustom = <a className="pl-[0.5em]" onClick={updateSort} data-sort="custom" data-change-order-only="1"><i className="fa fa-angle-up"></i></a>;
        } else {
            sortSymbolCustom = <a className="pl-[0.5em]" onClick={updateSort} data-sort="custom" data-change-order-only="1"><i className="fa fa-angle-down"></i></a>;
        }
    }

    let sortIgnore = [
        'weight', 'weight_total', 'amount', 'drink_amount', 'energy_output_kcal', 'energy_output_kj',
        'proteins', 'carbohydrates', 'fats', 'fats_omega3_dha_epa', 'energy_kcal', 'energy_kj', 'energy_balance_kcal', 'energy_balance_kj',
        'proteins_ratio', 'fats_saturated_unsaturated_ratio', 'fats_monounsaturated_polyunsaturated_ratio', 'fats_omega3_omega6_ratio',
        'fiber_ratio', 'calcium_magnesium_ratio', 'potassium_sodium_ratio',
        'energy', 'digestibility', 'acidobasicity', 'thermic_effect', 'biotin',
    ];
    let customSortOptions = [];
    for (let attr in globals.attributes) {
        if (sortIgnore.includes(attr)) continue;
        customSortOptions.push(
            <option value={attr} key={attr}>{globals.attributes[attr]}</option>
        );
    }

    const hdrNutrCls = 'flex w-[560px] text-[1em] justify-center pr-[2em] max-[829px]:hidden [&>div]:inline-flex [&>div]:items-center [&>div]:justify-center [&>div]:basis-[calc((100%-100px)/5)] [&>div]:pr-[1em] [&>div:last-child]:basis-[100px] [&>div>a]:underline [&>div>a]:text-[#737373] [&>div>a]:cursor-pointer';

    let recipesListHeader = (
        <div className="bg-[#fafafa] border-b border-[#eee] max-[829px]:hidden">
            <div className="flex items-center text-[0.75em] uppercase tracking-wide text-[#999] cursor-default [&>div]:py-[0.75em]">
                <div className="basis-[48px] h-auto flex items-center mr-[0.75em] shrink-0"></div>
                <div className="flex-1 flex">
                    <div className="flex-1 pl-[1em]">
                        <h3 className="m-0 font-normal text-[#737373]">{__('Název')}</h3>
                    </div>
                    <div className={hdrNutrCls}>
                        <div><a onClick={updateSort} data-sort="energy_kcal">{__('Kalorie')} {sortSymbols['energy_kcal'] || false}</a></div>
                        <div><a onClick={updateSort} data-sort="proteins">{__('Bílkoviny')} {sortSymbols['proteins'] || false}</a></div>
                        <div><a onClick={updateSort} data-sort="carbohydrates">{__('Sacharidy')} {sortSymbols['carbohydrates'] || false}</a></div>
                        <div><a onClick={updateSort} data-sort="fats">{__('Tuky')} {sortSymbols['fats'] || false}</a></div>
                        <div>
                            <select name="custom_sort" onChange={updateSort} data-sort="custom" className="text-[11px] w-[6em]" value={filter.customSortAttribute}>
                                {customSortOptions}
                            </select>
                            {sortSymbolCustom}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    let recipesList = [];
    if (recipes.length > 0) {
        Object.keys(recipes).map(key => {
            const recipe = recipes[key];

            let actionOpenButton = false;
            if (recipe.ownership === 'user') {
                if (typeof isEmbedded === 'undefined') {
                    actionOpenButton = <Link to={`/recipe/open/${recipe.id}`}>{__('Otevřít')}</Link>;
                }
            } else {
                if (Util.userHasPurchased('ZOF_COOKBOOK')) {
                    actionOpenButton = <Link to={`/recipe/open/${recipe.id}`}>{__('Otevřít')}</Link>;
                }
            }

            let tags = false;
            if (recipe.ownership === 'user') {
                tags = [];
                recipe.tag_ids.map(tagId => {
                    tags.push(globals.tags[tagId]);
                });
                if (tags.length > 0) {
                    tags = <div className="text-[0.85em] my-[4px]">{__('Štítky')}: {tags.join(', ')}</div>;
                }
            }

            let actionsButtons = false;

            let nutrData = [];
            if (recipe.nutritionData !== '') {
                nutrData.push(<div key="energy"><span>{__('Energie')}</span>{Math.ceil(recipe.nutritionData['energy_kcal'])} {globals.attribute_units['energy_kcal']}</div>);
                nutrData.push(<div key="proteins"><span>{__('Bílkoviny')}</span>{parseFloat(recipe.nutritionData['proteins'])} {globals.attribute_units['proteins']}</div>);
                nutrData.push(<div key="carbs"><span>{__('Sacharidy')}</span>{parseFloat(recipe.nutritionData['carbohydrates'])} {globals.attribute_units['carbohydrates']}</div>);
                nutrData.push(<div key="fats"><span>{__('Tuky')}</span>{parseFloat(recipe.nutritionData['fats'])} {globals.attribute_units['fats']}</div>);
                nutrData.push(<div key="custom"><span>{globals.attributes[filter.customSortAttribute]}</span>{parseFloat(recipe.nutritionData[filter.customSortAttribute])} {globals.attribute_units[filter.customSortAttribute]}</div>);
            }

            if (filter.viewMode === 'list') {
                recipesList.push(
                    <div key={recipe.id} className={recipeItemCls} onClick={(e) => onClick(recipe.id, e)}>
                        <div className={pictureCls} style={{ backgroundImage: `url(https://www.zofapp.cz/data/${recipe.thumbnail}?${recipe.picture_lastmod})` }}></div>
                        <div className="flex-1 flex max-[829px]:flex-wrap">
                            <div className="flex-1 pl-[1em] cursor-pointer max-[829px]:mb-[0.5em]">
                                <h3 className="m-0 text-[0.95em] font-semibold text-[#222]">{recipe.title}</h3>
                                {tags}
                                <div className="text-[12px] mt-[2px] [&>a]:inline-block [&>a]:mb-[4px] [&>a]:mr-[6px] [&>a]:text-[12px] [&>a]:text-center [&>a]:px-[6px] [&>a]:py-[4px] [&>a]:bg-white [&>a]:no-underline [&>a]:border [&>a]:border-[#26AA52] [&>a]:text-[#26AA52] [&>a]:rounded [&>a:hover]:bg-[#26AA52] [&>a:hover]:text-white">
                                    {actionsButtons}
                                </div>
                            </div>
                            <div className={nutrDataCls}>
                                {nutrData}
                            </div>
                        </div>
                    </div>
                );
            } else {
                recipesList.push(
                    <div key={recipe.id} className="flex flex-col basis-[calc(33.33%-1em)] mb-[1em] ml-[1em] max-[959px]:text-[0.9em] max-[719px]:basis-[calc(50%-1em)] max-[559px]:basis-[calc(100%-1em)]" onClick={(e) => onClick(recipe.id, e)}>
                        <div className="cursor-pointer basis-[200px] w-full bg-center bg-no-repeat bg-cover shrink-0" style={{ backgroundImage: `url(https://www.zofapp.cz/data/${recipe.thumbnail})` }}></div>
                        <div className="pt-[6px] text-[0.9em]">
                            <h3 className="m-0 text-[1em] font-normal underline cursor-pointer hover:text-[#26AA52]">{recipe.title}</h3>
                            <p className="text-[11px] mt-[4px] mb-0 text-[#555] max-[829px]:hidden"><span>{recipe.ownership === 'user' ? 'Můj recept' : 'ZOF recept'}</span></p>
                        </div>
                    </div>
                );
            }
        });
    }

    if (filter.viewMode === 'gallery') {
        recipesList = [
            <div key="gallery-wrap" className="flex flex-wrap ml-[-1em]">
                {recipesList}
            </div>
        ];
        recipesListHeader = false;
    }

    let pages = [];
    let numPages = Math.ceil(total / perPage);
    for (let p = 0; p < numPages; p++) {
        if (p === page) {
            pages.push(
                <strong key={p}>{(p + 1)}</strong>
            );
        } else {
            pages.push(
                <a key={p} onClick={(e) => changePage(e, p)}>{(p + 1)}</a>
            );
        }
    }

    let filter1 = (
        <span className="mr-[1.5em]">
            <em className="not-italic text-[0.9em]">{__('Zobrazení')}:</em>
            <a className={blLink(filter.viewMode === 'list')} onClick={() => setViewMode('list')}><i className="fa fa-list"></i></a>
            <a className={blLink(filter.viewMode === 'gallery')} onClick={() => setViewMode('gallery')}><i className="fa fa-th"></i></a>
        </span>
    );

    let filter2 = (
        <span className="mr-[1.5em]">
            <em className="not-italic text-[0.9em]">{__('Nutriční hodnoty')}:</em>
            <a className={blLink(filter.nutritionCalcMode === 'serving')} onClick={() => setNutritionCalcMode('serving')}>{__('porce')}</a>
            <a className={blLink(filter.nutritionCalcMode === '100g')} onClick={() => setNutritionCalcMode('100g')}>100 g</a>
        </span>
    );

    let filter3 = (
        <span className="mr-[1.5em]">
            <em className="not-italic text-[0.9em]">{__('Řazení')}:</em>
            <a className={blLink(filter.nutritionSortMode === 'popular')} onClick={() => setNutritionSortMode('popular')}>{__('populární')}</a>
            <a className={blLink(filter.nutritionSortMode === 'newest')} onClick={() => setNutritionSortMode('newest')}>{__('nejnovější')}</a>
        </span>
    );

    if (filter.viewMode === 'gallery') filter2 = false;

    let links = [];
    if (newRecipeLink === true) {
        let hasLocalSavedRecipe = false;
        let localRecipe = localStorage.getItem('zof_unsaved_recipe');
        if (localRecipe) {
            localRecipe = JSON.parse(localRecipe);
            if (typeof localRecipe === 'object') {

                let foodGroupToSearch = '1|0';

                let foodsNum = 0;
                if (typeof localRecipe.foods[foodGroupToSearch] !== 'undefined') {
                    localRecipe.foods[foodGroupToSearch].map(f => {
                        foodsNum++;
                    });
                }

                let titlesNum = 0;
                Object.keys(localRecipe.titles).map(t => {
                    if (localRecipe.titles[t] !== '') titlesNum++;
                });

                if (foodsNum > 0 && localRecipe.foods.constructor === Object) {
                    hasLocalSavedRecipe = true;
                }
                if (titlesNum > 0 && localRecipe.titles.constructor === Object) {
                    hasLocalSavedRecipe = true;
                }

                if (typeof localRecipe.foods[foodGroupToSearch] === 'undefined') {
                    hasLocalSavedRecipe = false;
                }
            }
        }

        if (!Util.isLectureLikeMode(data.appMode)) {
            if (hasLocalSavedRecipe) {
                links.push(<Link key="create" to="/recipe/create">Dokončit recept <span><i className="fa fa-angle-right"></i></span></Link>);
            } else {
                links.push(<Link key="create" to="/recipe/create">Nový recept <span><i className="fa fa-plus"></i></span></Link>);
            }
        }
    }

    let recipesContent = (
        <>
            {recipesListHeader}
            {recipesList}
        </>
    );

    if (loading) recipesContent = <Loading />;

    return (
        <div className="w-full flex flex-col">
            {!isEmbedded && (
                <div className="text-center py-[1.25em] pb-[0.75em] bg-[#eff1f7] relative">
                    <div className="max-w-[1180px] mx-auto px-[1em]">
                        <h1>Recepty <aside>{links}</aside></h1>
                    </div>
                </div>
            )}
            {globals.recipeFilters && globals.recipeFilters.userList && Object.keys(globals.recipeFilters.userList).length > 0 && (
                <div className="flex items-center justify-center bg-[#eff1f7]">
                    {Object.keys(globals.recipeFilters.userList).map(key => {
                        const isActive = filter.user_id.includes(key);
                        return <label key={key} className={`relative cursor-pointer top-[1px] z-[100] inline-block font-normal py-[0.9em] px-[2em] border border-[#e0e4eb] border-r-0 [&_input]:hidden first:rounded-tl-[10px] last:rounded-tr-[10px] last:border-r last:border-r-[#e0e4eb] ${isActive ? 'bg-white text-[#444] font-semibold border-b-white' : 'bg-[#ebebeb] text-[#404040] hover:bg-[#f1f1f1] hover:text-[#6c6c6c]'}`}><input type="checkbox" name="u" value={key} onChange={updateFilter} />{globals.recipeFilters.userList[key]}</label>;
                    })}
                </div>
            )}
            <div className="border-t border-[#e0e4eb] flex flex-1 flex-col">
                <div className="w-full px-[1em] max-w-[1180px] mx-auto">
                    <input type="checkbox" id="cb-filters-advanced" className="peer/filters hidden" />
                    <div className="pt-[1em] pb-[0.5em] bg-white">
                        <div className="relative flex">
                            <input className="flex-1 m-0 rounded-l-lg border-r-0 bg-white border border-[#e0e0e0] text-[1.1em] py-[0.85em] pr-[1.25em] pl-[3em] focus:outline-none focus:border-[#26AA52]" type="text" name="q" placeholder={__('Hledat v receptech...')} value={filter.q} onChange={updateFilter} />
                            <label className="flex items-center gap-[0.4em] text-[0.9em] py-[0.85em] px-[1.1em] rounded-r-lg cursor-pointer bg-white border border-[#e0e0e0] text-[#777] hover:bg-[#f5f5f5] hover:text-[#333]" htmlFor="cb-filters-advanced"><i className="fa fa-sliders-h text-[#26AA52]"></i> {__('filtr')}</label>
                        </div>
                    </div>

                    <div className="bg-[#f8f9fc] border border-[#eaecf2] rounded-lg mx-auto mb-[0.75em] max-h-0 overflow-hidden transition-all duration-[150ms] ease-out text-[0.85em] peer-checked/filters:max-h-[1000px] peer-checked/filters:duration-500 [&_p]:font-bold">
                        <div className="p-[1em]">
                            <div className="flex mb-[1em] [&>p]:basis-[120px] [&>p]:pt-[6px] [&>div]:flex-1">
                                <p>{__('Kategorie')}</p>
                                <div className={filtersItemCls}>
                                    {Object.keys(globals.tagsToGroups[1]).map(key => {
                                        const tagId = globals.tagsToGroups[1][key];
                                        return <label key={key}><input type="checkbox" name="t" value={tagId} onChange={updateFilter} /><span><i className="fa fa-check"></i></span> {globals.tags[tagId]}</label>;
                                    })}
                                </div>
                            </div>
                            <div className="flex mb-[1em] [&>p]:basis-[120px] [&>p]:pt-[6px] [&>div]:flex-1">
                                <p>{__('Stravovací režim')}</p>
                                <div className={filtersItemCls}>
                                    {Object.keys(globals.tagsToGroups[11]).map(key => {
                                        const tagId = globals.tagsToGroups[11][key];
                                        if ([98, 99].includes(tagId)) return false;
                                        return <label key={key}><input type="checkbox" name="t" value={tagId} onChange={updateFilter} /><span><i className="fa fa-check"></i></span> {globals.tags[tagId]}</label>;
                                    })}
                                </div>
                            </div>
                            <div className="flex mb-[1em] [&>p]:basis-[120px] [&>p]:pt-[6px] [&>div]:flex-1">
                                <p>{__('Chuť')}</p>
                                <div className={filtersItemCls}>
                                    {Object.keys(globals.tagsToGroups[10]).map(key => {
                                        const tagId = globals.tagsToGroups[10][key];
                                        return <label key={key}><input type="checkbox" name="t" value={tagId} onChange={updateFilter} /><span><i className="fa fa-check"></i></span> {globals.tags[tagId]}</label>;
                                    })}
                                </div>
                            </div>
                            <div className="flex items-center justify-end my-[1em] flex-wrap gap-y-[0.5em]">
                                {filter3}
                                {filter1}
                                {filter2}
                            </div>
                        </div>
                    </div>

                    <div>
                        {recipesContent}
                        <div className="my-[1.5em] flex items-center justify-center gap-[3px] [&>a]:inline-flex [&>a]:items-center [&>a]:justify-center [&>a]:min-w-[2em] [&>a]:h-[2em] [&>a]:px-[0.3em] [&>a]:rounded-md [&>a]:no-underline [&>a]:text-[#555] [&>a]:cursor-pointer [&>a:hover]:bg-[#eee] [&>strong]:inline-flex [&>strong]:items-center [&>strong]:justify-center [&>strong]:min-w-[2em] [&>strong]:h-[2em] [&>strong]:px-[0.3em] [&>strong]:rounded-md [&>strong]:bg-[#26AA52] [&>strong]:text-white">{pages}</div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default RecipeSearch;
